import React, { useCallback, useMemo, useState } from 'react'
import { SectionCard } from '@shared/components/panels/components/sections/SectionCard'
import { PanelTable } from '@shared/components/panels/components/display/PanelTable'
import type { PanelTableColumn } from '@shared/components/panels/components/display/PanelTable/types'
import type { TableAction } from '@shared/types/data-table'
import { Badge } from '@shared/components/ui/badge'
import { useVersionManagement } from '@shared/hooks/useVersionManagement'
import { VersionDetailSheet } from './VersionDetailSheet'
import { VersionRestoreDialog } from './VersionRestoreDialog'
import { formatDateOnly } from '@shared/utils/date'
import { toast } from 'sonner'
import type { Version, EntityType } from '@shared/types/versioned-entity.types'

export interface ScheduleVersionsSectionProps {
  entityType: EntityType
  entityId: string
  isDetailView?: boolean
  className?: string
  onVersionRestored?: () => void
}

const STATUS_VARIANT = {
  active: 'default' as const,
  draft: 'secondary' as const,
  archived: 'outline' as const,
}

export const ScheduleVersionsSection = React.memo<ScheduleVersionsSectionProps>(({
  entityType,
  entityId,
  isDetailView = false,
  className,
  onVersionRestored,
}) => {
  const {
    versions,
    loading,
    restoreVersion,
  } = useVersionManagement({
    entityType,
    entityId,
    autoFetch: true,
  })

  const [viewVersion, setViewVersion] = useState<Version | null>(null)
  const [restoreTargetVersion, setRestoreTargetVersion] = useState<Version | null>(null)
  const [isRestoring, setIsRestoring] = useState(false)

  const handleRestore = useCallback(async () => {
    if (!restoreTargetVersion) return

    setIsRestoring(true)
    try {
      await restoreVersion(restoreTargetVersion.id)
      toast.success(`Restored to version ${restoreTargetVersion.version}`)
      setRestoreTargetVersion(null)
      onVersionRestored?.()
    } catch {
      toast.error('Failed to restore version')
    } finally {
      setIsRestoring(false)
    }
  }, [restoreTargetVersion, restoreVersion, onVersionRestored])

  const handleRevertFromSheet = useCallback((versionId: string) => {
    const version = versions.find(v => v.id === versionId)
    if (version) {
      setViewVersion(null)
      setRestoreTargetVersion(version)
    }
  }, [versions])

  const columns = useMemo((): PanelTableColumn<Version>[] => [
    {
      key: 'version',
      label: 'VERSION',
      headerClassName: 'w-[80px]',
      render: (_value: unknown, item: Version) => (
        <span className="text-xs font-medium font-mono">
          v{item.version}
        </span>
      ),
    },
    {
      key: 'description',
      label: 'DESCRIPTION',
      render: (_value: unknown, item: Version) => (
        <span className="text-xs leading-4 truncate block max-w-[300px]" title={item.description || item.note || ''}>
          {item.description || item.note || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      headerClassName: 'w-[90px]',
      render: (_value: unknown, item: Version) => (
        <Badge variant={STATUS_VARIANT[item.status]} className="text-[10px] capitalize">
          {item.status}
        </Badge>
      ),
    },
    {
      key: 'createdByName',
      label: 'AUTHOR',
      headerClassName: 'w-[120px]',
      render: (_value: unknown, item: Version) => (
        <span className="text-xs text-muted-foreground">
          {item.createdByName || item.createdBy}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'DATE',
      headerClassName: 'w-[100px] text-right',
      cellClassName: 'text-right',
      render: (_value: unknown, item: Version) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatDateOnly(item.createdAt)}
        </span>
      ),
    },
  ], [])

  const rowActions = useCallback((item: Version): TableAction<Version>[] => {
    const actions: TableAction<Version>[] = [
      {
        key: 'view',
        label: 'View',
        onClick: () => setViewVersion(item),
      },
    ]

    if (item.status !== 'active') {
      actions.push({
        key: 'revert',
        label: 'Revert',
        onClick: () => setRestoreTargetVersion(item),
      })
    }

    return actions
  }, [])

  return (
    <>
      <SectionCard
        title="Version History"
        layout="down"
        className={className}
      >
        <PanelTable<Version>
          data={versions}
          columns={columns}
          rowActions={rowActions}
          variant={isDetailView ? 'full' : 'panel'}
          emptyMessage="No version history available"
          loading={loading}
          showPagination={versions.length > 10}
          pageSize={10}
        />
      </SectionCard>

      <VersionDetailSheet
        open={!!viewVersion}
        version={viewVersion}
        onClose={() => setViewVersion(null)}
        onRevert={handleRevertFromSheet}
      />

      <VersionRestoreDialog
        isOpen={!!restoreTargetVersion}
        onClose={() => setRestoreTargetVersion(null)}
        onConfirm={handleRestore}
        version={restoreTargetVersion}
        isLoading={isRestoring}
      />
    </>
  )
})

ScheduleVersionsSection.displayName = 'ScheduleVersionsSection'
